/*
 *  Recently Viewed Section
 *    Contains all the scripts used for the recently viewed products carousel.
 *
 *  Version:
 *    1.0.0 - 2024/01/01
 */
import { Section } from '../tools/section';
import { Swiper } from 'swiper';
import { Navigation, Scrollbar } from 'swiper/modules';
import { CLASS_ACTIVE } from "../tools/constants/classes";
import { ProductQuickView } from "../components/product-quick-view/ProductQuickView";
import AOS from 'aos';
import { rebindWishlist } from '../objects/swym-wishlist';


// Selectors
const SELECTOR_CONTAINER = '[data-recently-viewed]';
const SELECTOR_SWIPER_CONTAINER = '[data-recently-viewed-swiper]';
const SELECTOR_SWIPER_NEXT = '[data-recently-viewed-next]';
const SELECTOR_SWIPER_PREV = '[data-recently-viewed-prev]';
const SELECTOR_SWIPER_SCROLLBAR = '[data-recently-viewed-scrollbar]';
const SELECTOR_SWIPER_WRAPPER = '[data-recently-viewed-wrapper]';
const SELECTOR_CAROUSEL_ITEMS = '[data-recently-viewed-slide-item]';
const SELECTOR_TAB = '[data-recently-viewed-tab]';
const SELECTOR_TAB_ITEM = '[data-recently-viewed-tab-item]';
const SELECTOR_TAB_PANEL = '[data-recently-viewed-tab-panel]';
const SELECTOR_RECOMMENDED_SECTION = '[data-recently-viewed-recommended-section]';

// Constants
const COOKIE_NAME = 'recently_viewed_products';
const MAX_PRODUCTS = 10;

interface RecentlyViewedProduct {
  id: number;
  handle: string;
  title: string;
  price: number;
  compareAtPrice?: number;
  featuredImage: string;
  images: string[];
  url: string;
  tags: string[];
  variants?: Array<{
    id: number;
    title: string;
    price: number;
    compare_at_price?: number;
    available: boolean;
  }>;
  metafields?: {
    colour?: { title: string };
    usp_tags?: Array<{ title: string }>;
  };
}

interface ProductStorageData {
  id: number;
  handle: string;
}

class RecentlyViewed extends Section {
  private swiper: Swiper | null = null;
  private wrapper: HTMLElement | null = null;
  private recentlyViewedProducts: ProductStorageData[] = [];
  private resizeTimeout: number | null = null;
  private tabContainer: HTMLElement | null = null;
  private tabItems: NodeListOf<HTMLElement> | null = null;
  private tabPanels: NodeListOf<HTMLElement> | null = null;

  queryChildren(container: HTMLElement): void {
    this.wrapper = container.querySelector(SELECTOR_SWIPER_WRAPPER);
    this.tabContainer = container.querySelector(SELECTOR_TAB);
    this.tabItems = container.querySelectorAll(SELECTOR_TAB_ITEM);
    this.tabPanels = container.querySelectorAll(SELECTOR_TAB_PANEL);
    this.loadRecentlyViewedProducts();
  }

  attachEvents(): void {
    AOS.init({ duration: 800, easing: 'ease-out', once: true });
    
    // Listen for resize events to recalculate alignment
    window.addEventListener('resize', () => {
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }
      this.resizeTimeout = window.setTimeout(() => {
        this.checkCarouselAlignment();
        // Also check recommended carousel alignment on resize
        const recommendedSection = this.container.querySelector(SELECTOR_RECOMMENDED_SECTION) as HTMLElement;
        if (recommendedSection) {
          const swiperContainer = recommendedSection.querySelector('[data-swiper-container]') as HTMLElement;
          if (swiperContainer) {
            this.checkRecommendedCarouselAlignment(swiperContainer);
          }
        }
      }, 250);
    });

    this.attachTabEvents();
  }

  private resetHeaderAnimation(): void {
    if (!this.container) return;
    
    const header = this.container.querySelector('.c-recently-viewed__header');
    if (header) {
      header.classList.remove('aos-animate', 'aos-init');
      (header as HTMLElement).style.transitionDelay = '';
      (header as HTMLElement).style.animationDelay = '';
    }
  }

  private applyAOSStagger(): void {
    if (!this.container) return;
    
    const slides = this.container.querySelectorAll<HTMLElement>(SELECTOR_CAROUSEL_ITEMS);
    slides.forEach((el, idx) => {
      // Reset AOS classes first
      el.classList.remove('aos-animate', 'aos-init');
      el.style.transitionDelay = '';
      el.style.animationDelay = '';
      
      const delay = idx * 160; // ms - same timing as featured products
      // Ensure required attributes exist
      el.dataset.aos = el.dataset.aos || 'fade-up';
      el.dataset.aosOnce = 'true';
      el.setAttribute('data-aos-delay', String(delay));
      // Force the delay even if AOS doesn't re-read attributes
      el.style.transitionDelay = `${delay}ms`;
      el.style.animationDelay = `${delay}ms`;
    });
  }

  detatchEvents(): void {
    if (this.swiper) {
      this.swiper.destroy();
      this.swiper = null;
    }
    
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = null;
    }
    
    // Note: We don't remove the resize listener as it's shared across instances
  }

  private attachTabEvents(): void {
    if (!this.tabItems || this.tabItems.length === 0) return;

    this.tabItems.forEach(tabItem => {
      tabItem.addEventListener('click', (e) => {
        e.preventDefault();
        const targetTab = tabItem.getAttribute('data-recently-viewed-tab-item');
        if (targetTab) {
          this.switchTab(targetTab);
        }
      });
    });
  }

  private setupTabSwitching(): void {
    // Ensure proper initial tab state
    const recommendedTab = this.container.querySelector('[data-recently-viewed-tab-item="recommended"]') as HTMLElement;
    const recentlyViewedTab = this.container.querySelector('[data-recently-viewed-tab-item="recently-viewed"]') as HTMLElement;
    const recommendedPanel = this.container.querySelector('[data-recently-viewed-tab-panel="recommended"]') as HTMLElement;
    const recentlyViewedPanel = this.container.querySelector('[data-recently-viewed-tab-panel="recently-viewed"]') as HTMLElement;

    // Set recommended as active by default
    if (recommendedTab) recommendedTab.classList.add(CLASS_ACTIVE);
    if (recentlyViewedTab) recentlyViewedTab.classList.remove(CLASS_ACTIVE);
    if (recommendedPanel) {
      recommendedPanel.classList.add(CLASS_ACTIVE);
      recommendedPanel.style.display = 'block';
    }
    if (recentlyViewedPanel) {
      recentlyViewedPanel.classList.remove(CLASS_ACTIVE);
      recentlyViewedPanel.style.display = 'none';
    }
  }

  private switchTab(targetTab: string): void {
    if (!this.tabItems || !this.tabPanels) return;

    this.tabItems.forEach(tabItem => {
      const tabId = tabItem.getAttribute('data-recently-viewed-tab-item');
      if (tabId === targetTab) {
        tabItem.classList.add(CLASS_ACTIVE);
      } else {
        tabItem.classList.remove(CLASS_ACTIVE);
      }
    });

    this.tabPanels.forEach(panel => {
      const panelId = panel.getAttribute('data-recently-viewed-tab-panel');
      if (panelId === targetTab) {
        panel.classList.add(CLASS_ACTIVE);
        panel.style.display = 'block';
        
        this.refreshAOSForPanel(panel);
      } else {
        panel.classList.remove(CLASS_ACTIVE);
        panel.style.display = 'none';
      }
    });

    if (targetTab === 'recommended') {
      const recommendedSection = this.container.querySelector(SELECTOR_RECOMMENDED_SECTION) as HTMLElement;
      if (recommendedSection && recommendedSection.innerHTML.trim() === '') {
        this.loadRecommendedProducts();
      }
    }
  }

  private refreshAOSForPanel(panel: HTMLElement): void {
    const panelId = panel.getAttribute('data-recently-viewed-tab-panel');
    
    if (panelId === 'recently-viewed') {
      this.refreshAOSForRecentlyViewed(panel);
    } else if (panelId === 'recommended') {
      this.refreshAOSForRecommended(panel);
    }
  }

  private refreshAOSWithDelay(): void {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        AOS.refreshHard();
      });
    });
  }

  private executeOnNextFrame(callback: () => void): void {
    requestAnimationFrame(callback);
  }

  private refreshAOSForRecentlyViewed(panel: HTMLElement): void {
    const productItems = panel.querySelectorAll('.c-recently-viewed__product-item');
    productItems.forEach((el, idx) => {
      const htmlEl = el as HTMLElement;
      htmlEl.classList.remove('aos-animate', 'aos-init');
      htmlEl.style.transitionDelay = '';
      htmlEl.style.animationDelay = '';
      
      const delay = idx * 160; 
      htmlEl.dataset.aos = htmlEl.dataset.aos || 'fade-up';
      htmlEl.dataset.aosDelay = delay.toString();
      htmlEl.dataset.aosOnce = 'true';
    });
    
    this.refreshAOSWithDelay();
  }

  private refreshAOSForRecommended(panel: HTMLElement): void {
    const productItems = panel.querySelectorAll('.c-recently-viewed-recommendations__product-item');
    productItems.forEach((el, idx) => {
      const htmlEl = el as HTMLElement;
      htmlEl.classList.remove('aos-animate', 'aos-init');
      htmlEl.style.transitionDelay = '';
      htmlEl.style.animationDelay = '';
      
      const delay = idx * 50;
      htmlEl.dataset.aos = htmlEl.dataset.aos || 'fade-up';
      htmlEl.dataset.aosDelay = delay.toString();
      htmlEl.dataset.aosOnce = 'true';
    });
    
    this.refreshAOSWithDelay();
  }

  private initializeSwiperInstance(
    container: HTMLElement,
    nextButton: HTMLElement | null,
    prevButton: HTMLElement | null,
    scrollbar: HTMLElement | null
  ): Swiper {
    return new Swiper(container, {
      modules: [Scrollbar, Navigation],
      slidesPerView: 'auto',
      noSwipingSelector: '[data-swiper-no-swiping]',
      scrollbar: scrollbar
        ? { el: scrollbar, draggable: true }
        : undefined,
      navigation: {
        nextEl: nextButton || undefined,
        prevEl: prevButton || undefined,
      },
      keyboard: true,
      grabCursor: true,
      touchMoveStopPropagation: true,
    });
  }


  private initializeRecommendedSwiper(): void {
    const recommendedSection = this.container.querySelector(SELECTOR_RECOMMENDED_SECTION) as HTMLElement;
    if (!recommendedSection) return;

    const swiperContainer = recommendedSection.querySelector('[data-swiper-container]') as HTMLElement;
    if (!swiperContainer) return;


    const nextButton = swiperContainer.querySelector('[data-swiper-next]') as HTMLElement;
    const prevButton = swiperContainer.querySelector('[data-swiper-prev]') as HTMLElement;
    const scrollbar = swiperContainer.querySelector('[data-swiper-scrollbar]') as HTMLElement;
    
    // Ensure the swiper container is visible before initializing
    swiperContainer.style.display = 'block';
    
    const isMobile = window.innerWidth < 768;
    const delay = isMobile ? 400 : 0;
    
    setTimeout(() => {
      this.checkRecommendedCarouselAlignment(swiperContainer);
      this.initializeSwiperInstance(swiperContainer, nextButton, prevButton, scrollbar);
    }, delay);
  }

  private showRecommendedTab(): void {
    const tabContainer = this.container.querySelector(SELECTOR_TAB) as HTMLElement;
    const recommendedTab = this.container.querySelector('[data-recently-viewed-tab-item="recommended"]') as HTMLElement;
    const recommendedPanel = this.container.querySelector('[data-recently-viewed-tab-panel="recommended"]') as HTMLElement;
    const recentlyViewedTab = this.container.querySelector('[data-recently-viewed-tab-item="recently-viewed"]') as HTMLElement;
    const recentlyViewedPanel = this.container.querySelector('[data-recently-viewed-tab-panel="recently-viewed"]') as HTMLElement;

    // Check if we have recently viewed products
    const hasRecentlyViewedProducts = this.recentlyViewedProducts.length > 0;

    if (hasRecentlyViewedProducts) {
      if (tabContainer) {
        tabContainer.style.display = 'flex';
      }
      
      if (recommendedTab) {
        recommendedTab.style.display = 'block';
      }
      
      if (recentlyViewedTab) {
        recentlyViewedTab.style.display = 'block';
      }

      if (recommendedTab) {
        recommendedTab.classList.add(CLASS_ACTIVE);
      }
      if (recentlyViewedTab) {
        recentlyViewedTab.classList.remove(CLASS_ACTIVE);
      }
      if (recommendedPanel) {
        recommendedPanel.classList.add(CLASS_ACTIVE);
        recommendedPanel.style.display = 'block';
        recommendedPanel.style.visibility = 'visible';
      }
      if (recentlyViewedPanel) {
        recentlyViewedPanel.classList.remove(CLASS_ACTIVE);
        recentlyViewedPanel.style.display = 'none';
      }
    } else {
      if (tabContainer) {
        tabContainer.style.display = 'none';
      }
      
      if (recommendedPanel) {
        recommendedPanel.style.display = 'block';
        recommendedPanel.classList.add(CLASS_ACTIVE);
        recommendedPanel.style.visibility = 'visible';
      }
      if (recentlyViewedPanel) {
        recentlyViewedPanel.style.display = 'none';
        recentlyViewedPanel.classList.remove(CLASS_ACTIVE);
      }
    }
  }

  private loadRecommendedProducts(): void {
    if (!this.container) return;

    const recommendedSection = this.container.querySelector(SELECTOR_RECOMMENDED_SECTION) as HTMLElement;
    if (!recommendedSection) return;

    if (recommendedSection.innerHTML.trim() !== '') {
      const recommendations = recommendedSection.querySelector('[data-recently-viewed-recommendations]');
      const hasContent = recommendations && recommendations.innerHTML.trim().length > 0;
      
      if (hasContent) {
        this.initializeRecommendedSwiper();
        this.showRecommendedTab();
      } else {
        this.initializeRecommendedSection(recommendedSection);
      }
      return;
    }

    this.initializeRecommendedSection(recommendedSection);
  }

  private initializeRecommendedSection(recommendedSection: HTMLElement): void {
    const url = recommendedSection.getAttribute('data-url');
    if (!url) return;
  
    fetch(url)
      .then(response => response.text())
      .then(text => {
        const html = document.createElement('div');
        html.innerHTML = text;
        let recommendations = html.querySelector('[data-recently-viewed-recommendations]');
        const hasContent = recommendations && recommendations.innerHTML.trim().length > 0;
        const urlObj = new URL(url!, window.location.origin);
        const currentIntent = urlObj.searchParams.get('intent');
  
        if (!hasContent && currentIntent === 'complementary') {
          urlObj.searchParams.set('intent', 'related');
          fetch(urlObj.toString())
            .then(fallbackRes => fallbackRes.text())
            .then(fallbackText => {
              const fallbackHtml = document.createElement('div');
              fallbackHtml.innerHTML = fallbackText;
              const fallbackRecommendations = fallbackHtml.querySelector('[data-recently-viewed-recommendations]');
              if (fallbackRecommendations) {
                recommendedSection.innerHTML = '';
                recommendedSection.appendChild(fallbackRecommendations);
                this.initializeRecommendedSwiper();
                this.showRecommendedTab();
              }
            })
            .catch(e => console.error('Fallback fetch failed', e));
        } else if (hasContent) {
          recommendedSection.innerHTML = '';
          recommendedSection.appendChild(recommendations!);
          this.initializeRecommendedSwiper();
          this.showRecommendedTab();
        }
      })
      .catch(e => console.error(e));
  }

  private getCookie(name: string): string | null {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift() || null;
    }
    return null;
  }

  private setCookie(name: string, value: string, days: number = 30): void {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    const cookieString = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
    document.cookie = cookieString;
  }

  private getRecentlyViewedFromCookie(): ProductStorageData[] {
    const cookieValue = this.getCookie(COOKIE_NAME);
    if (!cookieValue) return [];
    
    try {
      const products = JSON.parse(decodeURIComponent(cookieValue));
      if (!Array.isArray(products)) return [];
      
      // Handle both old format (just IDs) and new format (objects with id and handle)
      const validProducts = products.map(item => {
        if (typeof item === 'number') {
          // Old format - just ID, we'll need to fetch handle later or skip
          return null;
        }
        if (typeof item === 'object' && item.id && item.handle) {
          return { id: item.id, handle: item.handle };
        }
        return null;
      }).filter(Boolean) as ProductStorageData[];
      
      return validProducts;
    } catch (error) {
      console.error('Error parsing cookie:', error);
      return [];
    }
  }

  private saveRecentlyViewedToCookie(products: ProductStorageData[]): void {
    const cookieValue = encodeURIComponent(JSON.stringify(products));
    this.setCookie(COOKIE_NAME, cookieValue);
  }

  private async fetchProductData(products: ProductStorageData[]): Promise<RecentlyViewedProduct[]> {
    if (products.length === 0) return [];

    try {
      const responses = await Promise.all(
        products.map(product => 
          fetch(`/products/${product.handle}.js`)
            .then(response => response.ok ? response.json() : null)
            .catch(() => null)
        )
      );

      // Since .js API doesn't include metafields, we'll use tags and other available data as fallbacks

      return responses.filter(Boolean).map((product, index) => {
        // Try to get stored metafield data from sessionStorage first
        let storedMetafields = null;
        try {
          const stored = sessionStorage.getItem(`product_metafields_${product.id}`);
          if (stored) {
            storedMetafields = JSON.parse(stored);
          }
        } catch (e) {
          // Failed to parse stored metafields, use fallback
        }

        // Fallback: Extract color from product title if not in stored metafields
        const titleParts = product.title.split(' ~ ');
        const extractedColor = titleParts.length > 1 ? titleParts[1] : '';
        
        // Use stored metafields if available, otherwise fallback to extracted data
        const colourMetafield = (storedMetafields as any)?.colour || (extractedColor ? { title: extractedColor } : null);
        const uspTagsMetafield = (storedMetafields as any)?.usp_tags || null;

        return {
          id: product.id,
          handle: product.handle,
          title: product.title,
          price: product.price,
          compareAtPrice: product.compare_at_price,
          featuredImage: product.featured_image,
          images: product.images || [],
          url: product.url,
          tags: product.tags || [],
          variants: product.variants || [],
          metafields: {
            colour: colourMetafield,
            usp_tags: uspTagsMetafield
          }
        };
      });
    } catch (error) {
      return [];
    }
  }

  private renderProductThumbnail(product: RecentlyViewedProduct): string {
    // Extract colour name from metafield
    const colourName = product.metafields?.colour?.title || '';
    
    // Format pricing and colour section using the same structure as product-thumbnail
    let priceAndColourSection = `
      <div class="o-product-thumbnail__price-and-colour">
        ${colourName ? `<span class="o-product-thumbnail__colour o-paragraph--2">${colourName}</span>` : ''}
        ${colourName ? `<span class="o-product-thumbnail__dot-divider"></span>` : ''}
    `;

    // Add pricing
    if (product.compareAtPrice && product.compareAtPrice > product.price) {
      priceAndColourSection += `
        <div class="o-product-thumbnail__pricing">
          <span class="o-product-thumbnail__pricing-compare">${this.formatPrice(product.compareAtPrice)}</span>
          <span>${this.formatPrice(product.price)}</span>
        </div>
      `;
    } else {
      priceAndColourSection += `
        <div class="o-product-thumbnail__pricing">
          <span>${this.formatPrice(product.price)}</span>
        </div>
      `;
    }

    priceAndColourSection += `</div>`;

    // Format USP tags section using the same structure as product-thumbnail
    let uspTagsSection = '';
    if (product.metafields?.usp_tags && Array.isArray(product.metafields.usp_tags) && product.metafields.usp_tags.length > 0) {
      const uspTags = product.metafields.usp_tags.slice(0, 2);
      uspTagsSection = `
        <div class="o-product-thumbnail__usp" data-swiper-no-swiping aria-labelledby="usp-heading-${product.id}">
          <p id="usp-heading-${product.id}" class="u-visually-hidden">Product highlights</p>
          <ul class="o-product-thumbnail__usp-list" role="list">
            ${uspTags.map(tag => {
              const tagText = typeof tag === 'string' ? tag : (tag.title || '');
              return `
                <li class="o-product-thumbnail__usp-item o-paragraph--3" data-usp="${tagText}">
                  <span class="o-product-thumbnail__usp-badge">${tagText}</span>
                </li>
              `;
            }).join('')}
          </ul>
        </div>
      `;
    }

    // Get secondary image for hover effect
    const secondaryImage = product.images && product.images.length > 1 ? product.images[1] : null;
    const primaryVariantId = product.variants && product.variants.length > 0 ? product.variants[0].id : product.id;
    
    return `
      <div class="o-product-thumbnail c-recently-viewed__product-item" data-product-thumbnail data-product-id="${product.id}" data-product-handle="${product.handle}" data-variant-id="${product.variants && product.variants.length > 0 ? product.variants[0].id : product.id}" data-recently-viewed-slide-item>
        <div class="o-product-thumbnail__inner">
          <div class="o-product-thumbnail__top-wrapper">
            <button type="button" class="swym-add-to-wishlist-view-product o-product-thumbnail__wishlist-btn" data-wishlist-button data-product="${product.id}" data-variant="${product.variants && product.variants.length > 0 ? product.variants[0].id : product.id}" data-url="${product.url}">
              <svg class="o-wishlist__wishlist-icon" viewBox="0 0 24 24" width="24" height="24">
                <path d="M12,21.35L10.55,20.03C5.4,15.36 2,12.27 2,8.5 2,5.41 4.42,3 7.5,3C9.24,3 10.91,3.81 12,5.08C13.09,3.81 14.76,3 16.5,3C19.58,3 22,5.41 22,8.5C22,12.27 18.6,15.36 13.45,20.03L12,21.35Z" fill="none" stroke="currentColor" stroke-width="1.5"/>
              </svg>
            </button>
            <button type="button" class="o-btn is-tertiary o-product-thumbnail__quick-view-btn" data-product-quick-view-btn>
              <span class="o-product-thumbnail__quick-view-btn-text o-paragraph--2">Quick View</span>
              <svg class="o-product-thumbnail__quick-view-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 5V19M5 12H19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>

            <a href="${product.url}" class="o-product-thumbnail__media-wrapper">
              <img src="${product.featuredImage}" 
                   alt="${product.title}" 
                   class="o-product-thumbnail__image o-media is-primary"
                   loading="lazy">
              ${secondaryImage ? `
                <img src="${secondaryImage}" 
                     alt="${product.title}" 
                     class="o-product-thumbnail__image o-media is-secondary"
                     loading="lazy">
              ` : ''}
            </a>
          </div>
          <div class="o-product-thumbnail__content">
            <a href="${product.url}" class="o-product-thumbnail__title o-heading--6">
              ${product.title}
            </a>
            ${priceAndColourSection}
            ${uspTagsSection}
          </div>
        </div>
      </div>
    `;
  }

  private formatPrice(price: number): string {
    // Use dynamic currency from Shopify settings for better internationalization
    // Falls back to AUD/en-AU for Australian store if Shopify settings are not available
    const shopifyCurrency = (window as any).Shopify?.currency?.active || 'AUD';
    const shopifyLocale = (window as any).Shopify?.locale || 'en-AU';
    
    try {
      return new Intl.NumberFormat(shopifyLocale, {
        style: 'currency',
        currency: shopifyCurrency
      }).format(price / 100);
    } catch (error) {
      // Fallback to AUD for Australian store if there's any currency formatting error
      return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD'
      }).format(price / 100);
    }
  }

  private checkCarouselAlignment(): void {
    if (!this.container) return;
    
    const wrapper = this.container.querySelector(SELECTOR_SWIPER_WRAPPER) as HTMLElement;
    const carousel = this.container.querySelector(SELECTOR_SWIPER_CONTAINER) as HTMLElement;
    const nextButton = this.container.querySelector(SELECTOR_SWIPER_NEXT) as HTMLElement;
    const prevButton = this.container.querySelector(SELECTOR_SWIPER_PREV) as HTMLElement;
    
    if (!wrapper || !carousel) return;

    // Wait for next frame to ensure products are rendered
    this.executeOnNextFrame(() => {
      const productItems = wrapper.querySelectorAll('.c-recently-viewed__product-item');
      
      if (productItems.length === 0) return;

      // Calculate total width of all products
      let totalProductsWidth = 0;
      productItems.forEach(item => {
        const htmlItem = item as HTMLElement;
        totalProductsWidth += htmlItem.offsetWidth;
      });

      // Add gap between products (col-gap)
      const computedStyle = getComputedStyle(wrapper);
      const gap = parseFloat(computedStyle.getPropertyValue('gap')) || 2; // Default 2px (.125rem)
      totalProductsWidth += gap * (productItems.length - 1);

      // Get container width (excluding padding)
      const containerWidth = carousel.clientWidth;
      
      // If total products width is less than container width, center it and hide arrows
      if (totalProductsWidth <= containerWidth) {
        wrapper.classList.add('no-slider');
        // Hide navigation arrows when centering
        if (nextButton) nextButton.style.display = 'none';
        if (prevButton) prevButton.style.display = 'none';
      } else {
        wrapper.classList.remove('no-slider');
        // Show navigation arrows when sliding is needed
        if (nextButton) nextButton.style.display = '';
        if (prevButton) prevButton.style.display = '';
      }
    });
  }

  private checkRecommendedCarouselAlignment(swiperContainer: HTMLElement): void {
    const wrapper = swiperContainer.querySelector('.swiper-wrapper') as HTMLElement;
    const productItems = wrapper.querySelectorAll('.swiper-slide');
    const nextButton = swiperContainer.querySelector('[data-swiper-next]') as HTMLElement;
    const prevButton = swiperContainer.querySelector('[data-swiper-prev]') as HTMLElement;

    if (!wrapper || productItems.length === 0) {
      if (nextButton) nextButton.style.display = 'none';
      if (prevButton) prevButton.style.display = 'none';
      return;
    }

    this.executeOnNextFrame(() => {
      let totalProductsWidth = 0;
      productItems.forEach(item => {
        const htmlItem = item as HTMLElement;
        totalProductsWidth += htmlItem.offsetWidth;
      });

      const computedStyle = getComputedStyle(wrapper);
      const gap = parseFloat(computedStyle.getPropertyValue('gap')) || 2; // Default 2px (.125rem)
      totalProductsWidth += gap * (productItems.length - 1);

      const containerWidth = swiperContainer.clientWidth;
      
      if (totalProductsWidth <= containerWidth) {
        wrapper.classList.add('no-slider');
        if (nextButton) nextButton.style.display = 'none';
        if (prevButton) prevButton.style.display = 'none';
      } else {
        wrapper.classList.remove('no-slider');
        if (nextButton) nextButton.style.display = '';
        if (prevButton) prevButton.style.display = '';
      }
    });
  }

  private initializeSwiper(): void {
    const container = this.container.querySelector(SELECTOR_SWIPER_CONTAINER) as HTMLElement;
    if (!container) return;

    // Check if carousel should be centered based on content width vs container width
    this.checkCarouselAlignment();

    const nextButton = container.querySelector(SELECTOR_SWIPER_NEXT) as HTMLElement;
    const prevButton = container.querySelector(SELECTOR_SWIPER_PREV) as HTMLElement;
    const scrollbar = container.querySelector(SELECTOR_SWIPER_SCROLLBAR) as HTMLElement;

    this.swiper = this.initializeSwiperInstance(container, nextButton, prevButton, scrollbar);
  }

  private async loadRecentlyViewedProducts(): Promise<void> {
    this.recentlyViewedProducts = this.getRecentlyViewedFromCookie();

    const showRecommendedProducts = this.container.getAttribute('data-show-recommended-products') === 'true';

    const recommendedTab = this.container.querySelector('[data-recently-viewed-tab-item="recommended"]') as HTMLElement;
    const recommendedPanel = this.container.querySelector('[data-recently-viewed-tab-panel="recommended"]') as HTMLElement;

    let hasRecentlyViewedProducts = false;
    if (this.recentlyViewedProducts.length > 0) {
      try {
        const products = await this.fetchProductData(this.recentlyViewedProducts);
        hasRecentlyViewedProducts = products.length > 0;
      } catch (error) {
        hasRecentlyViewedProducts = false;
      }
    }

    const tabContainer = this.container.querySelector('[data-recently-viewed-tab]') as HTMLElement;
    const recentlyViewedTab = this.container.querySelector('[data-recently-viewed-tab-item="recently-viewed"]') as HTMLElement;
    const recentlyViewedPanel = this.container.querySelector('[data-recently-viewed-tab-panel="recently-viewed"]') as HTMLElement;

    if (hasRecentlyViewedProducts && showRecommendedProducts) {
      if (tabContainer) tabContainer.style.display = 'flex';
      if (recentlyViewedTab) recentlyViewedTab.style.display = 'block';
      if (recommendedTab) recommendedTab.style.display = 'block';
      
      this.setupTabSwitching();
      this.loadRecommendedProducts();
      this.loadRecentlyViewedProductsForTabs();
      new ProductQuickView();
      return;
    } else if (hasRecentlyViewedProducts && !showRecommendedProducts) {
      if (tabContainer) tabContainer.style.display = 'none';
      if (recentlyViewedTab) recentlyViewedTab.style.display = 'none';
      if (recentlyViewedPanel) recentlyViewedPanel.style.display = 'none';
      
      this.loadRecentlyViewedProductsDirectly();
      new ProductQuickView();
      return;
    } else if (!hasRecentlyViewedProducts && showRecommendedProducts) {
      if (tabContainer) tabContainer.style.display = 'none';
      if (recentlyViewedTab) recentlyViewedTab.style.display = 'none';
      if (recentlyViewedPanel) recentlyViewedPanel.style.display = 'none';
      
      this.loadRecommendedProducts();
      new ProductQuickView();
      return;
    } else {
      this.container.style.display = 'none';
      return;
    }
  }

  private async loadRecentlyViewedProductsForTabs(): Promise<void> {
    if (this.recentlyViewedProducts.length === 0) return;

    try {
      const products = await this.fetchProductData(this.recentlyViewedProducts);
      
      if (products.length > 0) {
        this.renderProducts(products);
        this.initializeSwiper();

        this.renderProducts(products);
        this.container.style.display = 'block';
        this.initializeSwiper();
        
        if (typeof (window as any).refreshScrollTriggers === 'function') {
          (window as any).refreshScrollTriggers();
        }
        
        this.resetHeaderAnimation();
        this.refreshAOSWithDelay();
      }
    } catch (error) {
      console.error('Failed to load recently viewed products for tabs:', error);
    }
  }

  private async loadRecentlyViewedProductsDirectly(): Promise<void> {
    if (this.recentlyViewedProducts.length === 0) return;

    try {
      const products = await this.fetchProductData(this.recentlyViewedProducts);
      
      if (products.length > 0) {
        this.renderProducts(products);
        this.initializeSwiper();

        if (typeof (window as any).refreshScrollTriggers === 'function') {
          (window as any).refreshScrollTriggers();
        }
        
        this.resetHeaderAnimation();
        this.refreshAOSWithDelay();
      }
    } catch (error) {
      console.error('Failed to load recently viewed products directly:', error);
    }
  }

  private renderProducts(products: RecentlyViewedProduct[]): void {
    if (!this.wrapper) return;

    const productSlides = products.map(product => 
      `<div class="swiper-slide c-recently-viewed__carousel-slide">
         ${this.renderProductThumbnail(product)}
       </div>`
    ).join('');

    this.wrapper.innerHTML = productSlides;
    
    // Rebind wishlist functionality for the new content
    rebindWishlist();
    this.ensureQuickViewInstance();
    
    // Check alignment after products are rendered
    this.checkCarouselAlignment();
    
    // Apply staggered animation to the product items
    this.applyAOSStagger();
    
    // Refresh AOS to recognize new elements
    this.refreshAOSWithDelay();
  }

  private ensureQuickViewInstance(): void {
    const win = window as Window & { __productQuickViewInstance?: ProductQuickView };
    if (!win.__productQuickViewInstance) {
      win.__productQuickViewInstance = new ProductQuickView();
    }
  }

  // Public method to add product to recently viewed
  public static addProduct(productData: { id: number; handle: string }): void {
    const instance = new RecentlyViewed(document.createElement('div'), SELECTOR_CONTAINER);
    const currentProducts = instance.getRecentlyViewedFromCookie();
    
    // Remove the product if it already exists
    const filteredProducts = currentProducts.filter(product => product.id !== productData.id);
    
    // Add the product to the beginning
    const updatedProducts = [productData, ...filteredProducts].slice(0, MAX_PRODUCTS);
    
    instance.saveRecentlyViewedToCookie(updatedProducts);
  }

  // Public method to get recently viewed products
  public static getProducts(): ProductStorageData[] {
    const instance = new RecentlyViewed(document.createElement('div'), SELECTOR_CONTAINER);
    return instance.getRecentlyViewedFromCookie();
  }
}

// Register the section
Section.register(SELECTOR_CONTAINER, RecentlyViewed);

// Export for global use - make sure it's always available
(window as any).RecentlyViewed = RecentlyViewed;
